import React, { useState } from 'react';
import { contactInfo } from '../../data/mock';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Campi mancanti', description: 'Compila almeno nome, email e messaggio.' });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast({ title: 'Richiesta inviata!', description: 'Ti risponderemo entro 24 ore lavorative.' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 800);
  };

  return (
    <section id="contatti" className="py-24 lg:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="text-sky-600 text-xs font-bold uppercase tracking-[0.2em]">Contattaci</div>
          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Parla con un tecnico, oggi stesso</h2>
          <p className="mt-4 text-slate-600 text-lg">Compila il form per ricevere un preventivo gratuito o passa a trovarci in laboratorio. Rispondiamo entro 24 ore lavorative.</p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5" /></div>
                <div>
                  <div className="font-bold text-slate-900">Dove siamo</div>
                  <div className="text-slate-600 text-sm mt-1">{contactInfo.address}<br />{contactInfo.city}</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900">Telefono</div>
                  <a href={`tel:${contactInfo.phone.replace(/\s/g,'')}`} className="text-slate-600 text-sm mt-1 block hover:text-sky-600">{contactInfo.phone}</a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-900">WhatsApp</div>
                  <a
                    href={`https://wa.me/${contactInfo.whatsappRaw}?text=${encodeURIComponent(contactInfo.whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 text-sm mt-1 block hover:text-green-600"
                  >
                    {contactInfo.whatsapp}
                  </a>
                  <div className="text-xs text-slate-400 mt-0.5">Risposta rapida durante gli orari di apertura</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5" /></div>
                <div className="min-w-0">
                  <div className="font-bold text-slate-900">Email</div>
                  <ul className="mt-2 space-y-1.5">
                    {contactInfo.emails.map((e) => (
                      <li key={e.value}>
                        <div className="text-xs text-slate-500">{e.label}</div>
                        <a href={`mailto:${e.value}`} className="text-sm text-sky-700 hover:underline break-all">{e.value}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5" /></div>
                <div>
                  <div className="font-bold text-slate-900">Orari</div>
                  <ul className="mt-2 space-y-1">
                    {contactInfo.hours.map((h) => (
                      <li key={h.day} className="text-sm text-slate-600 flex justify-between gap-4">
                        <span>{h.day}</span><span className="font-medium text-slate-900">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={onSubmit} className="bg-white rounded-2xl p-7 lg:p-9 border border-slate-200 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Nome e cognome *</label>
                  <Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Mario Rossi" className="mt-1.5" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email *</label>
                  <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} placeholder="mario@email.it" className="mt-1.5" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Telefono</label>
                  <Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} placeholder="+39 ..." className="mt-1.5" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Tipo richiesta</label>
                  <Select value={form.subject} onValueChange={(v)=>setForm({...form, subject:v})}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="riparazione">Riparazione dispositivo</SelectItem>
                      <SelectItem value="informatica">Assistenza informatica</SelectItem>
                      <SelectItem value="rete">Rete aziendale</SelectItem>
                      <SelectItem value="telecomando">Duplicazione telecomando</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-slate-700">Messaggio *</label>
                <Textarea rows={5} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} placeholder="Descrivi il problema o la tua richiesta..." className="mt-1.5" />
              </div>
              <Button type="submit" disabled={loading} className="mt-6 bg-sky-600 hover:bg-sky-700 text-white rounded-full px-7 h-12">
                {loading ? 'Invio in corso...' : (<>Invia richiesta <Send className="w-4 h-4 ml-2" /></>)}
              </Button>
              <p className="mt-3 text-xs text-slate-500">Inviando accetti la nostra privacy policy. Non condividiamo i tuoi dati con terze parti.</p>
            </form>
          </div>
        </div>

        <div className="mt-12 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
          <iframe
            title="Mappa GT Assistenza Tecnica Taccone"
            src="https://www.google.com/maps?q=Via+Goffredo+Mameli+29,+20853+Biassono+MB&output=embed"
            className="w-full h-80 border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
}
